import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { toast } from "sonner";

const INSTAGRAM_HANDLE = "@cineglimpsestories";
const INSTAGRAM_URL = "https://instagram.com/cineglimpsestories";
const INQUIRY_EMAIL = "cineglimpsestories@gmail.com";

// Basic international phone: strip separators, require optional + and 7–15 digits (E.164).
const PHONE_RE = /^\+?\d{7,15}$/;
function isValidPhone(raw) {
  if (!raw) return false;
  return PHONE_RE.test(raw.replace(/[\s\-()]/g, ""));
}

const initialForm = { name: "", email: "", phone: "", event_date: "", event_location: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  function update(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please complete your name, email, phone and message.");
      return;
    }
    if (!isValidPhone(form.phone)) {
      toast.error("Please enter a valid phone number (7–15 digits, optionally starting with +).");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", form);
      setDone(true);
      setForm(initialForm);
      toast.success("Your inquiry has reached the studio.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send. Please email us directly.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="contact-page" className="bg-[#1A1514] min-h-screen">
      <section className="pt-40 md:pt-48 px-6 md:px-12 lg:px-16 max-w-7xl mx-auto">
        <Reveal>
          <p className="text-[10px] tracking-[0.42em] uppercase text-ink-taupe">Chapter IV</p>
        </Reveal>
        <Reveal delay={150}>
          <h1
            data-testid="contact-title"
            className="mt-6 font-display text-6xl md:text-8xl lg:text-9xl leading-none tracking-tighter text-ink-ivory"
          >
            Contact
          </h1>
        </Reveal>
        <Reveal delay={350}>
          <p className="mt-10 max-w-xl text-base md:text-lg leading-relaxed text-ink-ivory/65">
            We accept a small number of weddings each year. Tell us briefly about
            your day — we read every note and respond personally.
          </p>
        </Reveal>
      </section>

      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-20">
          {/* Studio details */}
          <Reveal className="md:col-span-4">
            <p className="text-[10px] tracking-[0.42em] uppercase text-ink-taupe">The Studio</p>
            <ul className="mt-8 space-y-6 text-base text-ink-ivory/80">
              <li>
                <p className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe mb-2">Email</p>
                <a
                  href={`mailto:${INQUIRY_EMAIL}`}
                  className="editorial-link font-display text-xl text-ink-ivory"
                  data-testid="contact-email-link"
                >
                  {INQUIRY_EMAIL}
                </a>
              </li>
              <li>
                <p className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe mb-2">Instagram</p>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="editorial-link font-display italic text-xl text-ink-ivory"
                  data-testid="contact-instagram-link"
                >
                  {INSTAGRAM_HANDLE}
                </a>
              </li>
              <li>
                <p className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe mb-2">Availability</p>
                <p className="text-ink-ivory/80">Worldwide · Limited dates each season</p>
              </li>
            </ul>
          </Reveal>

          {/* Inquiry form */}
          <div className="md:col-span-8">
            {done ? (
              <Reveal>
                <div
                  data-testid="inquiry-success"
                  className="border border-ink-ivory/10 px-8 md:px-12 py-16 md:py-20"
                >
                  <p className="text-[10px] tracking-[0.42em] uppercase text-ink-champagne">Received</p>
                  <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight text-ink-ivory">
                    Thank you — your note is with us.
                  </h2>
                  <p className="mt-8 text-base text-ink-ivory/70 leading-relaxed max-w-lg">
                    We read inquiries in the order they arrive and write back personally within a few days.
                    In the meantime, you can find quiet moments from the studio on Instagram.
                  </p>
                  <button
                    type="button"
                    data-testid="inquiry-send-another"
                    onClick={() => setDone(false)}
                    className="mt-10 luxe-btn"
                  >
                    Send another note
                  </button>
                </div>
              </Reveal>
            ) : (
              <Reveal>
                <form
                  onSubmit={onSubmit}
                  data-testid="inquiry-form"
                  className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10"
                  noValidate
                >
                  <Field label="Your name" required>
                    <input
                      data-testid="inquiry-name"
                      className="field-underline"
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Aria & Theo"
                      required
                    />
                  </Field>
                  <Field label="Email" required>
                    <input
                      data-testid="inquiry-email"
                      className="field-underline"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="hello@example.com"
                      autoComplete="email"
                      required
                    />
                  </Field>
                  <Field label="Phone number" required>
                    <input
                      data-testid="inquiry-phone"
                      className="field-underline"
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="+91 98765 43210"
                      autoComplete="tel"
                      inputMode="tel"
                      pattern="^\+?[\d\s\-()]{7,20}$"
                      required
                    />
                  </Field>
                  <Field label="Event date">
                    <input
                      data-testid="inquiry-date"
                      className="field-underline"
                      type="date"
                      value={form.event_date}
                      onChange={update("event_date")}
                    />
                  </Field>
                  <Field label="Location" className="md:col-span-2">
                    <input
                      data-testid="inquiry-location"
                      className="field-underline"
                      type="text"
                      value={form.event_location}
                      onChange={update("event_location")}
                      placeholder="Tuscany, Italy"
                    />
                  </Field>
                  <Field label="A short note" required className="md:col-span-2">
                    <textarea
                      data-testid="inquiry-message"
                      className="field-underline resize-none"
                      rows={5}
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Tell us about the day — the season, the people, the feeling you hope to keep."
                      required
                    />
                  </Field>
                  <div className="md:col-span-2 mt-4 flex items-center justify-between gap-6">
                    <p className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">
                      We reply personally.
                    </p>
                    <button
                      type="submit"
                      data-testid="inquiry-submit"
                      disabled={submitting}
                      className="luxe-btn disabled:opacity-50"
                    >
                      {submitting ? "Sending…" : "Send inquiry"}
                    </button>
                  </div>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">
        {label}{required && <span className="text-ink-champagne ml-1">·</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
